import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, ChevronRight, Info, Calendar, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { getCalendarEvents, findAvailableTimeSlots } from '../../utils/calendar';
import { useHealthMetrics } from '../../hooks/useHealthMetrics';
import { WorkoutScheduler } from './scheduling/WorkoutScheduler';

interface DailyWorkoutSuggestionsProps {
  onSchedule: (slot: any) => void;
}

export function DailyWorkoutSuggestions({ onSchedule }: DailyWorkoutSuggestionsProps) {
  const metrics = useHealthMetrics();
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [todaysWorkout, setTodaysWorkout] = useState<{
    id: string;
    name: string;
    time: string;
    duration: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const today = new Date();
        const events = await getCalendarEvents(today, today);
        
        // Find workout event for today
        const workoutEvent = events.find(event => 
          event.type === 'workout'
        );

        if (workoutEvent) {
          setTodaysWorkout({
            id: workoutEvent.id,
            name: workoutEvent.title,
            time: format(workoutEvent.start, 'h:mm a'),
            duration: Math.round((workoutEvent.end.getTime() - workoutEvent.start.getTime()) / (1000 * 60))
          });
        }

        const slots = findAvailableTimeSlots(events, today, 60, {
          earliestTime: '06:00',
          latestTime: '20:00',
          minimumGap: 45,
          preferredTimes: ['07:00', '12:00', '17:00'],
          blackoutPeriods: [{ start: '13:00', end: '14:00' }]
        });

        // Sort slots by preference and time
        const sortedSlots = slots.sort((a, b) => {
          if (a.isPreferred && !b.isPreferred) return -1;
          if (!a.isPreferred && b.isPreferred) return 1;
          return a.start.getTime() - b.start.getTime();
        });

        setAvailableSlots(sortedSlots);
      } catch (error) {
        console.error('Error fetching schedule:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchedule();
  }, []);

  const handleSchedule = (slot: any) => {
    setSelectedSlot(slot);
    setShowScheduler(true);
  };

  const handleReschedule = () => {
    setIsRescheduling(true);
    setShowScheduler(true);
  };

  if (showScheduler) {
    return (
      <WorkoutScheduler
        workout={todaysWorkout || { name: 'Workout', duration: 60 }}
        onClose={() => {
          setShowScheduler(false);
          setIsRescheduling(false);
        }}
        onScheduled={() => {
          setShowScheduler(false);
          setIsRescheduling(false);
          onSchedule(selectedSlot);
        }}
        isRescheduling={isRescheduling}
      />
    );
  }

  return (
    <div className="theme-card p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
      
      <div className="relative space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <h2 className="text-base font-medium">Smart Scheduler</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-primary">{metrics.recovery}% Recovery</div>
          </div>
        </div>

        {/* Today's Scheduled Workout */}
        {todaysWorkout && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative p-[1px] rounded-lg group"
          >
            {/* Siri-like Glow Effect */}
            <div className="absolute inset-0 rounded-lg animate-siri-glow">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-purple-500/30 to-primary/30 rounded-lg animate-border-flow" />
            </div>

            {/* Content */}
            <div className="relative bg-primary/10 backdrop-blur-sm p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Clock className="w-4 h-4 text-primary" />
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{todaysWorkout.name}</div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-primary font-medium">{todaysWorkout.time}</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">{todaysWorkout.duration} min</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleReschedule}
                  className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 px-2 py-1 rounded-lg bg-primary/20 text-xs text-primary hover:bg-primary/30"
                >
                  <CalendarIcon className="w-3 h-3" />
                  <span>Reschedule</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {isExpanded ? 'Hide' : 'Show'} Available Slots
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Available Time Slots */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-2 overflow-hidden"
            >
              {isLoading ? (
                <div className="text-sm text-muted-foreground text-center py-2">
                  Analyzing your calendar...
                </div>
              ) : availableSlots.length > 0 ? (
                availableSlots.map((slot, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-muted/30 p-3 rounded-lg flex items-center justify-between group hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span>{format(slot.start, 'h:mm a')}</span>
                      {slot.isPreferred && (
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                          Optimal Time
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleSchedule(slot)}
                      className="flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Schedule
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </motion.div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground text-center py-2">
                  No available slots today
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="w-3 h-3" />
          <span>Suggestions based on your calendar and optimal workout times</span>
        </div>
      </div>
    </div>
  );
}